import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { formatarDia as dia } from '@common/i18n/formatosData';
import { Alert, PageHeader, Skeleton } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMinhaParticipacao, useRecarregarParticipacao } from '../hooks';
import { FormularioCampos } from './FormularioCampos';
import { Respondido } from './Respondido';

const TOM_BLOQUEIO = {
  SEM_VINCULO: 'warning',
  NAO_PARTICIPANTE: 'warning',
  PARTICIPANTE_EXCLUIDO: 'warning',
  CAMPANHA_AGENDADA: 'info',
  CAMPANHA_FECHADA: 'info',
} as const;

/** Bloqueio (R18), "Respondido" (R16, R17) ou os passos (R6). */
export function FormularioPage() {
  const { campanhaId = '' } = useParams();
  const { t } = useTranslation(['formulario', 'erros']);
  const { data, isLoading, error } = useMinhaParticipacao(campanhaId);
  const recarregar = useRecarregarParticipacao(campanhaId);
  const [params, setParams] = useSearchParams();
  const [conflito, setConflito] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex w-full max-w-4xl flex-col gap-4" aria-busy="true">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="w-full max-w-4xl">
        <Alert variant="danger" title={t(`erros:${mensagemDeErro(error)}`)} />
      </div>
    );
  }

  const { campanha, situacao, motivo, dadosApresentados, resposta } = data;
  const aberta = situacao === 'pode_responder';
  const descricao = [
    dadosApresentados && t('formulario:saudacao', { nome: dadosApresentados.nomeExibicao }),
    aberta && t('formulario:abertaAte', { fim: dia(campanha.fim) }),
  ]
    .filter(Boolean)
    .join(' · ');
  const textoMotivo = motivo && t(`formulario:motivos.${motivo}`, { inicio: dia(campanha.inicio) });
  // R6: `?passo=` abre a edição; sem ele, com resposta salva, o "Respondido". Fechada, nunca edita (R17).
  const editando = aberta && (!resposta || params.has('passo'));

  return (
    <div className="flex w-full max-w-4xl flex-col gap-5">
      <PageHeader title={campanha.nome} description={descricao || undefined} />

      {situacao === 'bloqueado' && motivo && (
        <Alert variant={TOM_BLOQUEIO[motivo]}>{textoMotivo}</Alert>
      )}

      {situacao === 'somente_leitura' && motivo && (
        <Alert variant="info">
          {textoMotivo} {resposta && t('formulario:somenteLeitura')}
        </Alert>
      )}

      {!editando && conflito === 'RESPOSTA_DESATUALIZADA' && (
        <Alert variant="warning" title={t('erros:RESPOSTA_DESATUALIZADA')} />
      )}

      {situacao !== 'bloqueado' && !editando && (
        <Respondido
          participacao={data}
          onAlterar={
            aberta
              ? () => {
                  setConflito(null);
                  setParams((atuais) => {
                    const novos = new URLSearchParams(atuais);
                    novos.set('passo', 'brinde');
                    return novos;
                  });
                }
              : undefined
          }
        />
      )}

      {editando && (
        // Remonta ao mudar a versão (salvou, ou recarregou depois de um 409): valores novos sem `useEffect`.
        <FormularioCampos
          key={resposta?.versao ?? 0}
          participacao={data}
          onSalvo={() => setConflito(null)}
          onConflito={(codigo) => {
            setConflito(codigo === 'RESPOSTA_DESATUALIZADA' ? codigo : null);
            recarregar();
          }}
        />
      )}
    </div>
  );
}
